// --- Imports ---
import { useLayoutEffect, useRef, useState } from "react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { Box, Button, Stack, useTheme, alpha } from "@mui/material";
import { OpenInNew, Pause } from "@mui/icons-material";
import LiveProjectCard from "./LiveProjectCard";
import { useNavigate } from "react-router-dom";
import { liveProjectsData as livePrData } from "../../../../Data/liveProjects";

gsap.registerPlugin(ScrollTrigger);

// --- Live Projects Section ---
export default function LiveProjects() {
  const theme = useTheme();
  const navigate = useNavigate();
  const sectionRef = useRef(null);
  const trackRef = useRef(null);

  const [sectionHeight, setSectionHeight] = useState("100vh");
  const [isPlaying, setIsPlaying] = useState(true);

  const liveProjectsData = livePrData?.slice(0, 4);

  // --- Scroll Height Logic ---
  useLayoutEffect(() => {
    const updateHeight = () => {
      if (!trackRef?.current) return;
      const scrollDistance =
        trackRef?.current?.scrollWidth - window?.innerWidth;
      setSectionHeight(scrollDistance + window?.innerHeight);
      ScrollTrigger?.refresh();
    };

    updateHeight();
    window?.addEventListener("resize", updateHeight);

    return () => window?.removeEventListener("resize", updateHeight);
  }, []);

  // --- GSAP Horizontal Animation ---
  useLayoutEffect(() => {
    const ctx = gsap?.context(() => {
      gsap?.to(trackRef?.current, {
        x: () => -(trackRef?.current?.scrollWidth - window?.innerWidth),
        ease: "none",
        scrollTrigger: {
          trigger: sectionRef?.current,
          start: "top top",
          end: () => `+=${trackRef?.current?.scrollWidth - window?.innerWidth}`,
          pin: true,
          scrub: 1,
          anticipatePin: 1,
          invalidateOnRefresh: true,
          onLeave: () => {
            setSectionHeight("100vh !important");
          },
          onEnterBack: () => {
            const scrollDistance =
              trackRef?.current?.scrollWidth - window?.innerWidth;
            setSectionHeight(scrollDistance + window?.innerHeight);
          },
        },
      });
    }, sectionRef);

    return () => ctx?.revert();
  }, [liveProjectsData?.length]);

  const items = liveProjectsData?.map((project, idx) => (
    <LiveProjectCard
      key={idx}
      idx={idx}
      project={project}
      isPlaying={isPlaying}
      setIsPlaying={setIsPlaying}
      Pause={Pause}
    />
  ));

  return (
    <Box
      id="projects-section"
      ref={sectionRef}
      component="section"
      sx={{
        height: sectionHeight,
        overflow: "hidden",
        position: "relative",
        background: theme?.palette?.background?.default,
      }}
    >
      {/* --- See All Button --- */}
      <Button
        onClick={() => navigate("/projects")}
        startIcon={<OpenInNew />}
        sx={{
          position: "absolute",
          zIndex: 50,
          top: {xs:"10px" , md:"40px"},
          left: "50%",
          transform: "translateX(-50%)",
          background: `rgba(255, 255, 255, 0.05)`,
          backdropFilter: "blur(12px)",
          border: `1px solid ${alpha(theme?.palette?.primary?.main, 0.3)}`,
          color: theme?.palette?.text?.primary,
          padding: {xs:"8px 16px" , lg:"10px 24px"},
          borderRadius: "100px",
          fontWeight: 600,
          transition: "all 0.3s ease",
          fontSize:{ xs:".7rem" , lg:".8rem" } ,
          "&:hover": {
            background: alpha(theme?.palette?.primary?.main, 0.1),
            borderColor: theme?.palette?.primary?.main,
            transform: "translateX(-50%) translateY(-2px)",
            boxShadow: `0 10px 20px ${alpha(theme?.palette?.primary?.main, 0.2)}`,
          },
        }}
      >
        See All Projects
      </Button>

      {/* --- Track Container --- */}
      <Stack
        ref={trackRef}
        direction="row"
        sx={{
          height: "100vh",
          width: "fit-content",
        }}
      >
        {items}
      </Stack>
    </Box>
  );
}
